import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Landmark } from "lucide-react";

export default function BankTransferModal({ open, onClose, onSuccess }) {
  const { t, lang } = useApp();
  const [banks, setBanks] = useState([]);
  const [bankId, setBankId] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const [iban, setIban] = useState("SA");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
      setBankId(""); setBeneficiary(""); setIban("SA"); setAmount(""); setNote("");
    }
  }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!bankId || !beneficiary.trim() || !iban || !amt) return;
    setLoading(true);
    try {
      await api.post("/wallet/bank-transfer", {
        bank_id: bankId, iban: iban.replace(/\s/g, ""), beneficiary_name: beneficiary.trim(),
        amount: amt, note,
      });
      toast.success(t.toastBankTransfer);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="bank-transfer-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-primary" /> {t.bankTransfer}
          </DialogTitle>
          <DialogDescription>{t.bankTransferDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 pt-1">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.selectBank}</label>
            <Select value={bankId} onValueChange={setBankId}>
              <SelectTrigger data-testid="bank-select" className="h-12 rounded-2xl mt-1.5">
                <SelectValue placeholder={t.selectBank} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {banks.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`bank-${b.id}`}>
                    {lang === "ar" ? b.name_ar : b.name_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.beneficiaryName}</label>
            <Input data-testid="beneficiary-input" value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)}
              placeholder={t.beneficiaryPlaceholder} className="h-12 rounded-2xl mt-1.5" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.ibanLabel}</label>
            <Input data-testid="iban-input" dir="ltr" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())}
              placeholder={t.ibanPlaceholder} className="h-12 rounded-2xl mt-1.5 font-mono tracking-wider" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.amount}</label>
            <div className="relative mt-1.5">
              <Input data-testid="bank-amount-input" type="number" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder={t.enterAmount}
                className="h-12 rounded-2xl text-lg font-bold tabular-nums" />
              <span className="absolute top-1/2 -translate-y-1/2 end-4 text-sm font-bold text-muted-foreground">{t.currency}</span>
            </div>
          </div>
          <Button data-testid="bank-transfer-submit" onClick={submit}
            disabled={loading || !bankId || !beneficiary || !iban || !amount}
            className="w-full h-[52px] rounded-2xl text-base font-bold">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.transferBtn}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
